import { useState } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';

function computeNext(template, question, selectedOptionIds, alreadySeen) {
  const questions = (template.questions || []).sort((a, b) => a.order - b.order);
  const selectedOptions = (question.answer_options || []).filter(o => selectedOptionIds.includes(o.id));

  const newOutputItems = [];
  for (const opt of selectedOptions) {
    for (const item of (opt.output_items || [])) {
      newOutputItems.push({ ...item, source_question_id: question.id, source_option_id: opt.id });
    }
  }

  if (selectedOptions.every(o => o.branch_rule?.action === 'end')) {
    return { nextQuestion: null, outputItems: newOutputItems, complete: true };
  }

  const targetIds = selectedOptions
    .filter(o => o.branch_rule?.action === 'show_question' || o.branch_rule?.action === 'skip_to')
    .map(o => o.branch_rule?.target_question_id)
    .filter(Boolean);

  if (targetIds.length > 0) {
    const targets = targetIds
      .map(tid => questions.find(q => q.id === tid))
      .filter(q => q && !alreadySeen.includes(q.id))
      .sort((a, b) => a.order - b.order);
    return { nextQuestion: targets[0] || null, outputItems: newOutputItems, complete: !targets[0] };
  }

  const nextQ = questions.find(q => q.order > question.order && !alreadySeen.includes(q.id));
  return { nextQuestion: nextQ || null, outputItems: newOutputItems, complete: !nextQ };
}

const ITEM_LABELS = { tick: 'Tick', note: 'Note', write_in: 'Write-in', heading: 'Heading' };

export default function TemplatePreview({ template, onClose }) {
  const questions = (template.questions || []).sort((a, b) => a.order - b.order);
  const [currentQ, setCurrentQ] = useState(questions[0] || null);
  const [selected, setSelected] = useState([]);
  const [history, setHistory] = useState([]);
  const [outputItems, setOutputItems] = useState([]);
  const [alreadySeen, setAlreadySeen] = useState([]);
  const [complete, setComplete] = useState(false);

  if (!currentQ && !complete) return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-sm w-[500px] p-6 text-center">
        <p className="text-sm text-gray-500 mb-4">Template has no questions to preview.</p>
        <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-sm">Close</button>
      </div>
    </div>
  );

  const handleSelect = (optionId) => {
    if (!currentQ) return;
    if (currentQ.type === 'multi_choice') {
      setSelected(prev => prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]);
    } else {
      setSelected([optionId]);
    }
  };

  const handleNext = () => {
    if (selected.length === 0 || !currentQ) return;
    const result = computeNext(template, currentQ, selected, alreadySeen);

    setHistory(prev => [...prev, { question: currentQ, selected, outputItems: result.outputItems }]);
    setOutputItems(prev => [...prev, ...result.outputItems]);
    setAlreadySeen(prev => [...prev, currentQ.id]);

    if (result.complete || !result.nextQuestion) {
      setComplete(true);
      setCurrentQ(null);
    } else {
      setCurrentQ(result.nextQuestion);
      setSelected([]);
    }
  };

  const handleBack = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory(h => h.slice(0, -1));
    setOutputItems(items => items.slice(0, items.length - prev.outputItems.length));
    setAlreadySeen(s => s.filter(id => id !== prev.question.id));
    setCurrentQ(prev.question);
    setSelected(prev.selected);
    setComplete(false);
  };

  const groups = {};
  const groupOrder = [];
  const ungrouped = [];
  for (const item of outputItems) {
    if (item.section_group) {
      if (!groups[item.section_group]) { groups[item.section_group] = []; groupOrder.push(item.section_group); }
      groups[item.section_group].push(item);
    } else {
      ungrouped.push(item);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="template-preview-modal">
      <div className="bg-white rounded-sm w-[600px] max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div>
            <div className="text-sm font-medium text-gray-900">Template Preview</div>
            <div className="text-[10px] text-gray-400">Walk through as staff — no run is created</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {!complete && currentQ && (
            <>
              <div className="text-[11px] uppercase tracking-[0.06em] text-gray-400 mb-1">Question {history.length + 1} of ~{questions.length}</div>
              <h3 className="text-base font-medium text-gray-900 mb-1" data-testid="preview-question-text">{currentQ.text}</h3>
              {currentQ.help_text && <p className="text-xs text-gray-400 mb-3">{currentQ.help_text}</p>}

              <div className="space-y-2 mt-4 mb-6">
                {(currentQ.answer_options || []).sort((a, b) => a.order - b.order).map(opt => (
                  <button key={opt.id} onClick={() => handleSelect(opt.id)} data-testid={`preview-option-${opt.id}`}
                    className={`w-full text-left px-4 py-2.5 border rounded-sm text-sm transition-all ${
                      selected.includes(opt.id) ? 'border-2 font-medium' : 'border-gray-200 hover:border-gray-300'
                    }`} style={selected.includes(opt.id) ? { borderColor: 'var(--brand-hero)' } : {}}>
                    {opt.label}
                    {opt.output_items?.length > 0 && <span className="text-[10px] text-gray-300 ml-2">({opt.output_items.length} output items)</span>}
                  </button>
                ))}
              </div>
            </>
          )}

          {complete && (
            <div>
              <div className="text-center mb-5">
                <CheckCircle2 size={28} className="text-green-500 mx-auto mb-2" />
                <div className="text-base font-medium text-gray-900">Preview complete</div>
                <div className="text-xs text-gray-400">{outputItems.length} output items collected across {history.length} questions</div>
              </div>

              {outputItems.length > 0 && (
                <div className="border border-gray-100 rounded-sm p-3">
                  <div className="text-[11px] uppercase tracking-[0.06em] text-gray-400 mb-2">Generated checklist items</div>
                  {groupOrder.map(group => (
                    <div key={group} className="mb-3">
                      <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100 pb-1 mb-1">{group}</div>
                      {groups[group].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                          <span className={`px-1 py-0.5 rounded-sm text-[9px] ${item.type === 'tick' ? 'bg-blue-50 text-blue-600' : item.type === 'heading' ? 'bg-purple-50 text-purple-600' : item.type === 'note' ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>{ITEM_LABELS[item.type]}</span>
                          <span className="text-gray-700">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                  {ungrouped.length > 0 && (
                    <div className="mb-2">
                      <div className="text-[10px] font-medium text-gray-400 mb-1">Ungrouped</div>
                      {ungrouped.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                          <span className={`px-1 py-0.5 rounded-sm text-[9px] bg-gray-100 text-gray-500`}>{ITEM_LABELS[item.type]}</span>
                          <span className="text-gray-700">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
          <button onClick={handleBack} disabled={history.length === 0 && !complete} data-testid="preview-back-btn"
            className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-sm text-gray-500 hover:bg-gray-50 disabled:opacity-40">
            <ArrowLeft size={12} /> Back
          </button>
          {!complete ? (
            <button onClick={handleNext} disabled={selected.length === 0} data-testid="preview-next-btn"
              className="flex items-center gap-1 px-4 py-1.5 text-xs font-medium text-white rounded-sm disabled:opacity-60"
              style={{ background: 'var(--brand-primary, #1C3557)' }}>
              Next <ArrowRight size={12} />
            </button>
          ) : (
            <button onClick={onClose} data-testid="preview-close-btn"
              className="px-4 py-1.5 text-xs font-medium text-white rounded-sm"
              style={{ background: 'var(--brand-primary, #1C3557)' }}>
              Close preview
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
